/**
 * ClientsTable Component
 *
 * Data table displaying all clients with search and actions.
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2, Search, Plus, Users } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { DemoDisabled } from '@/components/ui/demo-disabled';
import type { Client } from '@/types/management';

interface ClientsTableProps {
  clients: Client[];
  isLoading: boolean;
  hasFetched: boolean;
  onEdit: (client: Client) => void;
  onDelete: (client: Client) => void;
  onAdd: () => void;
  /** When true, disables all mutation actions (add/edit/delete) */
  readOnly?: boolean;
}

/**
 * Sanitize client name to URL-safe slug
 */
function sanitizeClientSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function ClientsTable({
  clients,
  isLoading,
  hasFetched,
  onEdit,
  onDelete,
  onAdd,
  readOnly = false,
}: ClientsTableProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredClients = useMemo(() => {
    if (!searchQuery.trim()) {
      return clients;
    }
    const query = searchQuery.toLowerCase();
    return clients.filter(
      (client) =>
        client.name.toLowerCase().includes(query) ||
        client.aliases.some((alias) => alias.toLowerCase().includes(query)) ||
        client.description?.toLowerCase().includes(query),
    );
  }, [clients, searchQuery]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    [],
  );

  // Show skeleton while loading OR before first successful fetch completes
  if (isLoading || (clients.length === 0 && !hasFetched)) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <Skeleton className="h-10 w-full max-w-sm" />
          <Skeleton className="h-10 w-[120px]" />
        </div>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Aliases</TableHead>
                <TableHead className="hidden md:table-cell">
                  Description
                </TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <Skeleton className="h-4 w-48" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-8 w-16" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  // Empty state only after we've confirmed there are no clients (hasFetched is true)
  if (clients.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="No clients yet"
        description={
          readOnly
            ? 'No clients have been configured for this demo.'
            : 'Create your first client to start organizing documents and conversations.'
        }
        action={
          <DemoDisabled disabled={readOnly}>
            <Button onClick={onAdd} disabled={readOnly}>
              <Plus className="h-4 w-4 mr-2" />
              Add Client
            </Button>
          </DemoDisabled>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search clients..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="pl-9"
          />
        </div>
        <DemoDisabled disabled={readOnly}>
          <Button onClick={onAdd} disabled={readOnly}>
            <Plus className="h-4 w-4 mr-2" />
            Add Client
          </Button>
        </DemoDisabled>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Aliases</TableHead>
              <TableHead className="hidden md:table-cell">
                Description
              </TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredClients.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="h-24 text-center text-muted-foreground"
                >
                  No clients match your search.
                </TableCell>
              </TableRow>
            ) : (
              filteredClients.map((client) => (
                <TableRow key={client.id}>
                  <TableCell className="font-medium">
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/management/documents/${sanitizeClientSlug(client.name)}`,
                        )
                      }
                      className="hover:underline text-left"
                    >
                      {client.name}
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-[200px]">
                      {client.aliases.length > 0 ? (
                        client.aliases.slice(0, 3).map((alias) => (
                          <Badge
                            key={alias}
                            variant="secondary"
                            className="text-xs"
                          >
                            {alias}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                      {client.aliases.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{client.aliases.length - 3}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell max-w-[300px] truncate">
                    {client.description || (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <DemoDisabled disabled={readOnly}>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEdit(client)}
                          aria-label={`Edit ${client.name}`}
                          disabled={readOnly}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </DemoDisabled>
                      <DemoDisabled disabled={readOnly}>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDelete(client)}
                          aria-label={`Delete ${client.name}`}
                          className="text-destructive hover:text-destructive"
                          disabled={readOnly}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </DemoDisabled>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Results count */}
      {!isLoading && clients.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {searchQuery
            ? `Showing ${filteredClients.length} of ${clients.length} clients`
            : `${clients.length} client${clients.length !== 1 ? 's' : ''}`}
        </p>
      )}
    </div>
  );
}
